import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { AddOnItemName } from "schemas/dashboard";

import { useCheckout } from "components/StripeCheckout/useCheckout";

const styles = {
  card: {
    backgroundColor: "white",
    borderRadius: { xs: 0, md: 3 },
    p: { xs: 0, md: 2 },
    boxShadow: { xs: 0, md: 1 },
  },
  saveBundle: {
    ml: 0.5,
    position: "relative",
    top: "-0.5em",
    color: "success.light",
    fontWeight: 700,
    fontSize: 8,
    mt: -4,
  },
  iconButton: {
    fontSize: 12,
    color: "black",
    width: 20,
    height: 20,
  },
};

interface Props {
  addOnItemName: AddOnItemName;
}

const VaAddOn = ({ addOnItemName }: Props) => {
  const isMobileScreen = useMediaQuery("(max-width:1600px)");

  const { setAlert } = useContext(AlertContext);
  const { addOnItems, applyAddOnLoading } = useContext(CheckoutContext);
  const { addOns } = useContext(SubscriptionContext);
  const { applyAddOnItem, getQuantityOfAddOnFromInvoice } = useCheckout();

  const addOnNicheQuantity =
    getQuantityOfAddOnFromInvoice(AddOnItemName.niche) + 2 || 2;

  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string>("");

  const exists = addOnItems?.find((x) => x.name === addOnItemName);

  const increaseQuantity = () => {
    if (quantity >= addOns[addOnItemName]?.max_quantity) {
      setError(
        `This item is limit to ${addOns[addOnItemName]?.max_quantity} per order.`,
      );
      return;
    } else {
      setError("");
      setQuantity(quantity + 1);
    }
  };

  const resetQuantity = () => {
    setQuantity(1);
    setError("");
  };

  const addToOrderButton = exists ? (
    <Box sx={{ px: 2, fontSize: 10, color: "success.light" }}>
      <strong>
        Added <Box component="i" className="fa-regular fa-check" />
      </strong>
    </Box>
  ) : (
    <LoadingButton
      size="small"
      sx={{ ml: 1 }}
      loading={applyAddOnLoading === addOnItemName}
      onClick={() => {
        let errorMessage = "";
        if (
          addOnItemName === AddOnItemName.pitching_templates &&
          quantity > addOnNicheQuantity
        ) {
          errorMessage = `Your number of templates cannot exceed your number of niches. Lower your template count to ${addOnNicheQuantity} or add more niches.`;
          setError(errorMessage);
          setAlert(errorMessage, "warning");
          return;
        }
        setError("");
        applyAddOnItem({
          name: addOnItemName,
          quantity,
        });
      }}
    >
      + Add
    </LoadingButton>
  );

  const itemQuantity =
    addOnItemName !== AddOnItemName.support_call ? (
      <Grid
        gap={1}
        container
        item
        xs={isMobileScreen ? "auto" : 2}
        alignItems="center"
        justifyContent={"flex-end"}
      >
        {exists ? (
          <Typography sx={{ fontSize: 12, color: grey[500] }}>
            Quantity: {quantity}
          </Typography>
        ) : (
          <>
            <IconButton sx={styles.iconButton} onClick={resetQuantity}>
              <Box component="i" className="fa-regular fa-trash" />
            </IconButton>
            <Typography>{quantity}</Typography>
            <IconButton sx={styles.iconButton} onClick={increaseQuantity}>
              <Box component="i" className="fa-regular fa-plus" />
            </IconButton>
          </>
        )}
      </Grid>
    ) : (
      <></>
    );

  return (
    <Grid>
      <Grid sx={styles.card} container alignItems="center">
        {!isMobileScreen && (
          <Grid container item lg={3} xl={2} sx={{ ml: -2 }}>
            {addToOrderButton}
          </Grid>
        )}
        <Grid item xs>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontSize: 14 }}>
              <strong>{addOns[addOnItemName]?.display_name} </strong>
            </Typography>
          </Grid>
          <Typography sx={{ color: grey[500], fontSize: 12 }}>
            {addOns[addOnItemName]?.description}
          </Typography>

          <FormHelperText sx={{ fontSize: 10, color: "error.main" }}>
            <em>{error}</em>
          </FormHelperText>

          {isMobileScreen && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2, ml: -2 }}
            >
              {addToOrderButton}
              {itemQuantity}
            </Grid>
          )}
        </Grid>
        {!isMobileScreen && itemQuantity}
      </Grid>
    </Grid>
  );
};

export default VaAddOn;
